/**
 * Sticky header compact state (ported from New-Man).
 * `.site-header` is `position: sticky` in CSS; this only toggles `.is-stuck`.
 * The compact state is shorter than the full header, so a hysteresis window (on at 200px,
 * off below 40px) prevents scroll anchoring from flipping the state back and forth.
 *
 * A sticky element reserves space in the document flow equal to its own current box size, so
 * shrinking `.site-header` on `.is-stuck` used to shift every section below it up by the height
 * difference. `--site-header-height` records the full (not-stuck) height and `_site-header.scss`
 * applies it as `min-height` at all times, so the reserved space never changes; only the visible
 * chrome inside compacts.
 */
const stickyHeader = () => {
	const header = document.querySelector('[data-sticky-header]');
	if (!header) return;

	const ON = 200;
	const OFF = 40;

	// Measures the header's natural height with the compact state momentarily removed, so
	// resizing (e.g. rotating a phone) while already scrolled past ON still yields the full,
	// not-stuck height rather than the current compact one.
	const measureNaturalHeight = () => {
		const wasStuck = header.classList.contains('is-stuck');
		if (wasStuck) header.classList.remove('is-stuck');
		const height = header.offsetHeight;
		if (wasStuck) header.classList.add('is-stuck');
		header.style.setProperty('--site-header-height', `${height}px`);
	};

	const update = () => {
		const y = window.scrollY;
		if (!header.classList.contains('is-stuck') && y > ON) {
			header.classList.add('is-stuck');
		} else if (header.classList.contains('is-stuck') && y < OFF) {
			header.classList.remove('is-stuck');
		}
	};

	let resizeTimer;
	const onResize = () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(measureNaturalHeight, 150);
	};

	measureNaturalHeight();
	update();
	window.addEventListener('scroll', update, { passive: true });
	window.addEventListener('resize', onResize, { passive: true });
};

export default stickyHeader;
